import { useMutation, useQueryClient } from "react-query";

import { useAgentsHttp } from "../useAgentsHttp";
import { type PostJsonError } from "../http/postJson";
import type { Project } from "../models/project";

// Backend `POST /projects/put` is an UPSERT: omitted fields stay as
// whatever the prior row carried; provided fields overwrite. The
// caller is responsible for only sending fields that changed (or
// fields it wants to preserve) - blank-out semantics happen by
// sending an explicit null, not by omitting.
//
// Body shape is the same `Project` Pydantic model; the FE builds it
// from local form state via `metaToProjectPayload` in
// `ProjectDetailsRoute`.
//
// Two extra top-level keys the FE adds for concurrency / actor
// stamping (NOT fields on the Project model; the backend route
// strips them before validating):
//
// - `expected_version` (optional): the version the FE loaded into
//   the form. Backend writes conditionally on this — if another
//   operator updated the row in the meantime, the call returns 409
//   with `{ error: "version_conflict", payload: { current_project }}`
//   and the FE refetches + asks the operator to redo the change.
//   Omit on first-create / bulk paths where racing is acceptable.
//
// - `updated_by` (optional): actor stamp. DEV_USER_ID today;
//   Kinde principal once auth lands.
export interface UpsertProjectInput {
  // Partial Project + required project_id. We accept Partial here
  // so the call site can send just the fields it edited; the server
  // model.validates the full row server-side anyway.
  project_id: string;
  expected_version?: number;
  updated_by?: string;
  [field: string]: unknown;
}

// Thrown by useUpsertProject's mutate on a 409. Carries the
// server-confirmed fresh row so the caller can show the operator
// what someone else just wrote.
export class ProjectVersionConflictError extends Error {
  readonly current_project: Project;
  constructor(message: string, current_project: Project) {
    super(message);
    this.name = "ProjectVersionConflictError";
    this.current_project = current_project;
  }
}

interface VersionConflictBody {
  error: "version_conflict";
  message: string;
  payload?: { current_project: Project };
}

export const useUpsertProject = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<Project, Error, UpsertProjectInput>(
    async (payload) => {
      try {
        return await http<Project>("/projects/put", payload);
      } catch (e) {
        const err = e as PostJsonError;
        if (err.status === 409 && err.bodyText) {
          // Try to parse the structured 409 - if successful, surface
          // a typed error the FE can pattern-match on. If parsing
          // fails (older backend, garbled body), fall through to the
          // generic error.
          try {
            const parsed = JSON.parse(err.bodyText) as VersionConflictBody;
            if (parsed?.payload?.current_project) {
              throw new ProjectVersionConflictError(
                parsed.message ?? "Another operator updated this project; please review.",
                parsed.payload.current_project,
              );
            }
          } catch (jsonErr) {
            if (jsonErr instanceof ProjectVersionConflictError) throw jsonErr;
            // JSON parse failed; fall through to original error.
          }
        }
        throw e;
      }
    },
    {
      onSuccess: (saved) => {
        // Drop the project-get cache so a subsequent useProject(id)
        // fetches the freshly-saved row; invalidate the list so
        // Operations sees any newly-populated fields.
        qc.invalidateQueries({
          queryKey: ["inrange-agents-project-get", saved.project_id],
        });
        qc.invalidateQueries({
          queryKey: ["inrange-agents-projects-list"],
        });
      },
      onError: (err, vars) => {
        // On a 409 the backend's `current_project` is the freshly-
        // persisted row. Seed the get cache with it so the next
        // useProject() render shows the operator the new state
        // without an extra round-trip.
        if (err instanceof ProjectVersionConflictError) {
          qc.setQueryData(
            ["inrange-agents-project-get", vars.project_id],
            err.current_project,
          );
        }
      },
    },
  );
};
